<script>
import SelectedItemsDisplay from '@modules/global/components/selected-items-display/selected-items-display.vue'
import { normalizeText } from '@utils/Format'

export default {
  name: 'SelectSearch',
  components: { SelectedItemsDisplay },
  model: { prop: 'value', event: 'input' },
  props: {
    value: {
      type: [Array, String, Number, Object],
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
    returnBy: {
      type: String,
      default: 'auto',
      validator: (v) => ['auto', 'id', 'value'].includes(v),
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    selectionPlaceholder: {
      type: String,
      default: 'Todos',
    },
    searchPlaceholder: {
      type: String,
      default: 'Pesquisar',
    },
    maxWidth: {
      type: [String, Number],
      default: null,
    },
    minWidth: {
      type: [String, Number],
      default: null,
    },
    menuMaxWidth: {
      type: [String, Number],
      default: 375,
    },
    items: {
      type: Array,
      default: () => [],
    },
    keyItem: {
      type: String,
      default: 'value',
    },
    keyId: {
      type: String,
      default: 'id',
    },
    keySort: {
      type: String,
      default: '',
    },
    preserveItemsOrder: {
      type: Boolean,
      default: false,
    },
    itemsPerLoad: {
      type: Number,
      default: 50,
    },
    initialSelected: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    single: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    requireSelection: {
      type: Boolean,
      default: false,
    },
    selectionText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      search: '',
      show: false,
      displayedItems: [],
      selectedItems: [],
      permanentSelectedItems: [],
      lastInteractedId: null,
      hasBeenBlurred: false,
      isBootstrapping: false,
      inClamp: false,
      selectedChangedWhileOpen: false,
    }
  },
  computed: {
    isEmptyList() {
      return this.listItems.length === 0
    },
    isEmptySourceList() {
      return this.formattedItemsList.length === 0
    },
    listItems() {
      let data = this.formattedItemsList
      if (this.search && this.search.length) data = this.filterItems(data)
      if (!this.preserveItemsOrder) data = this.sortItems(data)
      return data
    },
    formattedItemsList() {
      return this.formatItems(this.items)
    },
    // id interno -> id custom do item CRU. `formatItems` reescreve `id`/`value`
    // nas cópias, então o item normalizado não é fonte confiável do id custom
    // (ver getVModelOutById). Memoiza o lookup para não varrer `items` a cada
    // seleção — listas de cliente passam de mil opções.
    rawCustomIdByInternalId() {
      const map = new Map()
      if (!Array.isArray(this.items) || this.keyId === 'id') return map

      for (const item of this.items) {
        if (!item || typeof item !== 'object') continue
        if (!Object.prototype.hasOwnProperty.call(item, this.keyId)) continue

        const hasId = this.hasUsableId(item)
        const internalId = hasId ? item.id : item[this.keyId]
        // Primeiro item vence, igual à varredura linear que isto substitui —
        // preserva o desempate dos casos de colisão id x keyId.
        if (!map.has(internalId)) map.set(internalId, item[this.keyId])
      }

      return map
    },
    selectedData() {
      return this.selectedItems
        .map((id) => {
          const item = this.formattedItemsList.find((i) => i.id === id)
          return item
            ? {
                id: item.id,
                value: item.value,
              }
            : null
        })
        .filter(Boolean)
    },
    selectedItemsValues() {
      return this.getSelectedValuesById(this.items, this.selectedItems)
    },
    showClearSelectedItems() {
      return (
        !this.loading &&
        !this.disabled &&
        !this.requireSelection &&
        this.selectedItems.length > 0
      )
    },
    showSelectedItemsList() {
      return (
        this.permanentSelectedItems.length > 0 &&
        this.formattedItemsList.length > 10
      )
    },
    validationValue() {
      const values = this.selectedItemsValues
      return this.single ? values[0] || '' : values
    },
    normalizedMaxWidth() {
      return this.normalizeSize(this.maxWidth)
    },
    normalizedMinWidth() {
      return this.normalizeSize(this.minWidth)
    },
    normalizedMenuMaxWidth() {
      return this.normalizeSize(this.menuMaxWidth)
    },
    isPlainStringItems() {
      return (
        Array.isArray(this.items) &&
        this.items.length > 0 &&
        this.items.every((x) => typeof x === 'string')
      )
    },
    effectiveReturnBy() {
      if (this.isPlainStringItems) return 'value'
      if (this.returnBy !== 'auto') return this.returnBy
      return 'id'
    },
    preferCustomIdFirst() {
      return this.effectiveReturnBy === 'id' && this.keyId !== 'id'
    },
    menuMinWidth() {
      void this.show
      const el = this.$refs.activator
      const w = el && el.offsetWidth ? el.offsetWidth : 0
      return w ? w + 'px' : 'auto'
    },
  },
  watch: {
    selectedItems: {
      handler(newVal) {
        if (this.handleSelectedItemsClamp(newVal)) return
        if (this.handleSelectedItemsRequireSelection(newVal)) return
        if (this.isBootstrapping) return
        this.emitSelection()
        if (this.hasBeenBlurred && this.$refs.input)
          this.$refs.input.validate(true)
      },
      deep: true,
    },
    selected(newVal) {
      this.applySelectedProp({ selectedValue: newVal })
      if (this.hasBeenBlurred && this.$refs.input)
        this.$refs.input.validate(true)
    },
    value(newVal) {
      this.applySelectedProp({ selectedValue: newVal })
      if (this.hasBeenBlurred && this.$refs.input)
        this.$refs.input.validate(true)
    },
    single(newVal) {
      if (!newVal) return
      if (Array.isArray(this.selectedItems) && this.selectedItems.length > 1) {
        const trimmed = [this.preferredId(this.selectedItems)]
        if (!this.areArraysEqual(this.selectedItems, trimmed)) {
          this.inClamp = true
          this.selectedItems = trimmed
        }
      }
    },
    show(newValue) {
      if (newValue) {
        this.handleMenuOpen()
      } else {
        this.closeAndCommit()
      }
    },
    search() {
      this.displayedItems = this.listItems.slice(0, this.itemsPerLoad)
    },
    listItems: {
      handler() {
        this.displayedItems = this.listItems.slice(0, this.itemsPerLoad)
      },
      immediate: true,
    },
    items() {
      if (this.isBootstrapping) return
      const source = this.pickInitialSource()
      if (source !== null) {
        this.applySelectedProp({ selectedValue: source })
      } else {
        this.initializeSelection()
      }
      this.ensureDefaultRequiredSelection()
    },
    requireSelection(newVal) {
      if (!newVal) return
      this.ensureDefaultRequiredSelection()
    },
  },
  mounted() {
    this.bootstrapSelection()
    this.resetVuetifyValidation()
  },
  methods: {
    bootstrapSelection() {
      this.isBootstrapping = true
      const source = this.pickInitialSource()
      if (source !== null) {
        this.applySelectedProp({ selectedValue: source })
      } else {
        this.initializeSelection()
      }
      this.ensureSingleClamp()
      this.ensureDefaultRequiredSelection()
      this.isBootstrapping = false
    },
    emitSelection() {
      const out =
        this.effectiveReturnBy === 'value'
          ? this.getVModelOutByValue()
          : this.getVModelOutById()
      this.$emit('input', out)
      this.$emit('updateSelected', this.selectedData)
      this.$emit('update:selected', [...this.selectedItems])
      if (this.show) this.selectedChangedWhileOpen = true
      if (this.hasBeenBlurred && this.$refs.input)
        this.$refs.input.validate(true)
    },
    pickInitialSource() {
      if (this.hasSelectedProp()) return this.selected
      if (this.hasValueProp()) return this.value
      return null
    },
    hasSelectedProp() {
      const s = this.selected
      return (
        (Array.isArray(s) && s.length > 0) ||
        (typeof s === 'string' && s.length > 0) ||
        typeof s === 'number'
      )
    },
    hasValueProp() {
      const v = this.value
      if (Array.isArray(v)) return v.length > 0
      if (typeof v === 'string') return v.length > 0
      if (typeof v === 'number') return true
      if (v && typeof v === 'object') {
        return (
          Object.prototype.hasOwnProperty.call(v, 'id') ||
          Object.prototype.hasOwnProperty.call(v, 'value')
        )
      }
      return false
    },
    ensureSingleClamp() {
      if (!this.single) return
      if (Array.isArray(this.selectedItems) && this.selectedItems.length > 1) {
        const trimmed = [this.preferredId(this.selectedItems)]
        if (!this.areArraysEqual(this.selectedItems, trimmed)) {
          this.inClamp = true
          this.selectedItems = trimmed
        }
      }
    },
    handleSelectedItemsClamp(newVal) {
      if (this.inClamp) {
        this.inClamp = false
        if (!this.isBootstrapping) this.emitSelection()
        return true
      }
      if (this.single && Array.isArray(newVal) && newVal.length > 1) {
        const trimmed = [this.preferredId(newVal)]
        if (!this.areArraysEqual(newVal, trimmed)) {
          this.inClamp = true
          this.selectedItems = trimmed
          return true
        }
      }
      return false
    },
    handleSelectedItemsRequireSelection(newVal) {
      if (
        !this.requireSelection ||
        !Array.isArray(newVal) ||
        newVal.length !== 0 ||
        this.formattedItemsList.length === 0
      )
        return false
      const hasLast =
        this.lastInteractedId &&
        this.formattedItemsList.some((i) => i.id === this.lastInteractedId)
      const fallbackId = hasLast
        ? this.lastInteractedId
        : this.formattedItemsList[0].id
      this.inClamp = true
      this.selectedItems = [fallbackId]
      return true
    },
    ensureDefaultRequiredSelection() {
      if (
        !this.requireSelection ||
        !Array.isArray(this.selectedItems) ||
        this.selectedItems.length > 0 ||
        this.formattedItemsList.length === 0
      )
        return
      this.inClamp = true
      this.selectedItems = [this.formattedItemsList[0].id]
    },
    applySelectedProp({ selectedValue }) {
      const filtered = this.coerceToIds(selectedValue)
      if (this.areArraysEqual(this.selectedItems, filtered)) return
      this.selectedItems = filtered
    },
    coerceToIds(input) {
      const arr = this.normalizeSelectedToArray(input)
      const { idMap, byCustomId, byValue } = this.buildIdResolvers()
      const flat = arr
        .map((el) => this.resolveToIds({ el, idMap, byCustomId, byValue }))
        .reduce((acc, v) => acc.concat(v), [])
      return this.single && flat.length > 1 ? [flat[flat.length - 1]] : flat
    },
    buildIdResolvers() {
      const { idMap, byValue } = this.makeIdAndValueMaps()
      const byCustomId = this.makeByCustomIdMap()
      return { idMap, byCustomId, byValue }
    },
    makeIdAndValueMaps() {
      const idMap = new Map()
      const byValue = new Map()
      for (const it of this.formattedItemsList) {
        idMap.set(String(it.id), it.id)
        const k = String(it.value)
        const list = byValue.get(k)
        if (list) list.push(it.id)
        else byValue.set(k, [it.id])
      }
      return { idMap, byValue }
    },
    makeByCustomIdMap() {
      const map = new Map()
      const raw = Array.isArray(this.items) ? this.items : []
      for (let i = 0; i < raw.length; i += 1) {
        const it = raw[i]
        if (!it || typeof it !== 'object') continue
        const hasCustom =
          this.keyId !== 'id' &&
          Object.prototype.hasOwnProperty.call(it, this.keyId)
        if (!hasCustom) continue
        map.set(
          String(it[this.keyId]),
          this.hasUsableId(it) ? it.id : it[this.keyId]
        )
      }
      return map
    },
    resolveToIds({ el, idMap, byCustomId, byValue }) {
      if (el && typeof el === 'object') {
        return this.idsFromObject({ obj: el, idMap, byCustomId, byValue })
      }
      const k = String(el)
      if (this.preferCustomIdFirst && byCustomId.has(k))
        return [byCustomId.get(k)]
      if (idMap.has(k)) return [idMap.get(k)]
      if (!this.preferCustomIdFirst && byCustomId.has(k))
        return [byCustomId.get(k)]
      return byValue.get(k) || []
    },
    idsFromObject({ obj, idMap, byCustomId, byValue }) {
      if (
        this.preferCustomIdFirst &&
        Object.prototype.hasOwnProperty.call(obj, this.keyId)
      ) {
        const k = String(obj[this.keyId])
        if (byCustomId.has(k)) return [byCustomId.get(k)]
      }
      if (Object.prototype.hasOwnProperty.call(obj, 'id')) {
        const k = String(obj.id)
        if (idMap.has(k)) return [idMap.get(k)]
      }
      if (
        !this.preferCustomIdFirst &&
        Object.prototype.hasOwnProperty.call(obj, this.keyId)
      ) {
        const k = String(obj[this.keyId])
        if (byCustomId.has(k)) return [byCustomId.get(k)]
      }
      if (Object.prototype.hasOwnProperty.call(obj, 'value')) {
        return byValue.get(String(obj.value)) || []
      }
      return []
    },
    getVModelOutById() {
      const mapId = (id) => {
        const item = this.formattedItemsList.find((i) => i.id === id)
        if (!item) return null
        if (this.keyId === 'id') return item.id
        // O item CRU vem antes do normalizado de propósito: `formatItems`
        // reescreve `id` e `value` nas cópias, então quando `keyId` colide com
        // uma dessas chaves — caso do BaseFilterMenu, que passa keyId="value"
        // para options `{ text, value }` — `item[keyId]` devolveria o RÓTULO no
        // lugar do identificador, e o backend recebia nome em vez de id.
        const rawCustomId = this.findRawCustomId({ internalId: item.id })
        if (rawCustomId !== null && rawCustomId !== undefined)
          return rawCustomId
        if (Object.prototype.hasOwnProperty.call(item, this.keyId))
          return item[this.keyId]
        return item.id
      }
      const out = this.selectedItems.map(mapId).filter((v) => v !== null)
      return this.single ? (out[0] === undefined ? null : out[0]) : out
    },
    findRawCustomId({ internalId }) {
      if (!this.rawCustomIdByInternalId.has(internalId)) return null
      return this.rawCustomIdByInternalId.get(internalId)
    },
    getVModelOutByValue() {
      const values = this.selectedItems
        .map((id) => {
          const f = this.formattedItemsList.find((i) => i.id === id)
          return f ? f.value : null
        })
        .filter(Boolean)
      return this.single ? values[0] || null : values
    },
    normalizeSize(v) {
      if (typeof v === 'number') return v + 'px'
      if (typeof v === 'string') {
        const t = v.trim()
        if (/^\d+$/.test(t)) return t + 'px'
        return t
      }
      return ''
    },
    initializeSelection() {
      const emptySelection = []
      if (!this.isValidInitialSetup()) {
        this.selectedItems = emptySelection
        return
      }
      const normalizedSelected = this.initialSelected
        .filter((val) => typeof val === 'string' || typeof val === 'number')
        .map((val) => String(val))
      let selectedInitializeItems = this.formattedItemsList
        .filter((item) => normalizedSelected.includes(String(item.value)))
        .map((item) => item.id)
      if (this.single && selectedInitializeItems.length > 1) {
        selectedInitializeItems = [
          selectedInitializeItems[selectedInitializeItems.length - 1],
        ]
      }
      this.selectedItems = selectedInitializeItems
    },
    preferredId(ids) {
      if (!Array.isArray(ids) || ids.length === 0) return null
      if (this.lastInteractedId && ids.includes(this.lastInteractedId))
        return this.lastInteractedId
      return ids[ids.length - 1]
    },
    updatePermanentSelectedItemsOnOpen() {
      this.permanentSelectedItems = [...this.selectedData]
    },
    clearSelectedItems() {
      if (this.requireSelection) return
      this.selectedItems = []
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop
      const clientHeight = event.target.clientHeight
      const scrollHeight = event.target.scrollHeight
      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100
      const scrollThreshold = 85
      if (scrollPercentage >= scrollThreshold) this.loadMoreItems()
    },
    loadMoreItems() {
      const nextItems = this.getNextItems()
      this.addItemsToDisplay(nextItems)
    },
    getNextItems() {
      return this.listItems.slice(
        this.displayedItems.length,
        this.displayedItems.length + this.itemsPerLoad
      )
    },
    addItemsToDisplay(nextItems) {
      const hasMoreItemsToDisplay = nextItems.length > 0
      if (hasMoreItemsToDisplay)
        this.displayedItems = [...this.displayedItems, ...nextItems]
    },
    getSelectedValuesById(items, selectedIds) {
      const formatted = this.formatItems(items)
      return selectedIds
        .map((id) => {
          const found = formatted.find((item) => item.id === id)
          return found ? found.value : null
        })
        .filter(Boolean)
    },
    formatItems(items) {
      if (!Array.isArray(items)) return []
      return items
        .filter((item) => item !== null && item !== undefined)
        .map((item, index) => {
          let copy
          if (typeof item === 'string') {
            copy = { value: item }
          } else if (typeof item === 'object') {
            copy = Object.assign({}, item)
          } else {
            return null
          }
          copy = this.addId(copy, index)
          copy = this.addValue(copy)
          return copy
        })
        .filter(Boolean)
    },
    // Predicado do id interno: `0` e `false` são identificadores legítimos;
    // null, undefined, '' e NaN não. Não governa o rótulo (`value`) — ver o
    // comentário de `setItemProperty`.
    hasUsableValue(value) {
      if (value === null || value === undefined || value === '') return false
      // `NaN` como id interno seria irresolvível (`NaN !== NaN`): o item ficaria
      // selecionado internamente e invisível para `selectedData`/`getVModelOutById`.
      return !Number.isNaN(value)
    },
    // `id` só serve como identificador interno quando existe E tem valor. Payloads
    // de JOIN externo (ex: /aawz-partners/professionals) trazem a coluna `id` com
    // null para as linhas sem par — tratar esse null como id válido faz o mapa
    // custom-id → id devolver null e a seleção some ao ser reaplicada pelo pai.
    hasUsableId(item) {
      if (!item || typeof item !== 'object') return false
      if (!Object.prototype.hasOwnProperty.call(item, 'id')) return false
      return this.hasUsableValue(item.id)
    },
    addId(item, index) {
      if (!item || typeof item !== 'object') return item
      const isMissingId = !this.hasUsableId(item)
      if (isMissingId) {
        this.setItemProperty({ item, index, key: 'id', customKey: this.keyId })
      }
      return item
    },
    addValue(item) {
      if (!item || typeof item !== 'object') return item
      if (!Object.prototype.hasOwnProperty.call(item, 'value')) {
        this.setItemProperty({ item, key: 'value', customKey: this.keyItem })
      }
      return item
    },
    setItemProperty({ item, index = null, key, customKey }) {
      const hasCustomKey =
        customKey &&
        customKey !== key &&
        Object.prototype.hasOwnProperty.call(item, customKey)
      const raw = item[customKey]
      // O rótulo (`value`) mantém o `||` de propósito: quem o consome —
      // getVModelOutByValue, getSelectedValuesById, validationValue e
      // getItemValue — ainda trata 0/false como vazio, então promover esses
      // valores aqui faria o item sumir do v-model e do display. Alinhar os
      // quatro é mudança de comportamento própria.
      const value =
        key === 'id' ? this.resolveIdValue({ raw, index }) : raw || 'Sem valor'
      item[key] = value
      if (hasCustomKey) delete item[customKey]
    },
    // `hasUsableValue` (e não `||`) para que `0` e `false` promovidos do
    // customKey sobrevivam como ID: com `||`, um `id_profissional: 0` viraria o
    // índice da lista e divergiria dos mapas de resolução, que o indexam como 0.
    resolveIdValue({ raw, index }) {
      return this.hasUsableValue(raw) ? raw : index
    },
    sortItems(items) {
      const key = this.keySort || 'value'
      return items.sort((a, b) => {
        const aValue = this.getItemValue(a, key)
        const bValue = this.getItemValue(b, key)
        const numberComparison = this.compareByNumber(aValue, bValue)
        if (numberComparison !== 0) return numberComparison
        const textComparison = this.compareLexicographically(aValue, bValue)
        if (textComparison !== 0) return textComparison
        return this.compareByValue(a, b)
      })
    },
    getItemValue(item, key) {
      return (item[key] || '').toString().trim().toLowerCase()
    },
    extractNumber(str) {
      const match = String(str).match(/\d+/)
      return match ? parseInt(match[0], 10) : NaN
    },
    compareByNumber(aValue, bValue) {
      const aNumber = this.extractNumber(aValue)
      const bNumber = this.extractNumber(bValue)
      return isNaN(aNumber) || isNaN(bNumber) ? 0 : aNumber - bNumber
    },
    compareLexicographically(aValue, bValue) {
      return String(aValue).localeCompare(String(bValue), 'pt', {
        sensitivity: 'base',
      })
    },
    compareByValue(a, b) {
      const aValueSecondary = this.getItemValue(a, 'value')
      const bValueSecondary = this.getItemValue(b, 'value')
      return this.compareLexicographically(aValueSecondary, bValueSecondary)
    },
    // Busca tolerante a acento: normaliza os DOIS lados da comparação, senão
    // "jose" não encontra "José" — comportamento inaceitável numa base de nomes
    // brasileiros. A normalização só amplia o conjunto de matches: tudo que
    // casava com o `toLowerCase().includes()` anterior continua casando.
    filterItems(data) {
      const query = normalizeText(this.search)
      return data.filter((d) => normalizeText(String(d.value)).includes(query))
    },
    isValidInitialSetup() {
      return (
        Array.isArray(this.initialSelected) &&
        Array.isArray(this.items) &&
        this.initialSelected.length > 0
      )
    },
    normalizeSelectedToArray(selectedValue) {
      if (Array.isArray(selectedValue)) return selectedValue
      if (selectedValue === null || selectedValue === undefined) return []
      return [selectedValue]
    },
    areArraysEqual(a, b) {
      if (!Array.isArray(a) || !Array.isArray(b)) return false
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false
      }
      return true
    },
    isSelectableItem(item) {
      return (
        item &&
        typeof item.value === 'string' &&
        this.initialSelected.includes(item.value)
      )
    },
    handleItemToggle(id) {
      this.lastInteractedId = id
    },
    selectSingle(id) {
      this.lastInteractedId = id
      const already =
        Array.isArray(this.selectedItems) && this.selectedItems[0] === id
      if (this.requireSelection && already) {
        this.$emit('reselect', id)
        this.show = false
        return
      }
      this.selectedItems = already ? [] : [id]
      if (!already && this.selectedItems.length > 0) {
        this.show = false
      }
    },
    resetDisplayed() {
      this.displayedItems = this.listItems.slice(0, this.itemsPerLoad)
    },
    clearDisplayed() {
      this.displayedItems = []
    },
    handleMenuOpen() {
      this.selectedChangedWhileOpen = false
      if (this.loading || this.disabled) return
      this.resetDisplayed()
      setTimeout(() => {
        if (this.$refs.searchField) this.$refs.searchField.focus()
      }, 300)
      this.updatePermanentSelectedItemsOnOpen()
      this.scrollToSelectedOption()
    },
    scrollToSelectedOption() {
      if (!Array.isArray(this.selectedItems) || this.selectedItems.length === 0)
        return
      const targetId = this.selectedItems[0]
      const targetIndex = this.listItems.findIndex(
        (item) => item.id === targetId
      )
      if (targetIndex === -1) return
      if (targetIndex >= this.displayedItems.length)
        this.displayedItems = this.listItems.slice(
          0,
          targetIndex + this.itemsPerLoad
        )
      setTimeout(() => {
        const container = this.$refs.listContainer
        if (!container) return
        const option = Array.prototype.find.call(
          container.children,
          (el) => el.dataset.itemId === String(targetId)
        )
        if (!option) return
        const target =
          container.scrollTop +
          option.getBoundingClientRect().top -
          container.getBoundingClientRect().top
        this.$vuetify.goTo(target, {
          container,
          duration: 400,
          easing: 'easeInOutCubic',
        })
      }, 300)
    },
    resetVuetifyValidation() {
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.resetValidation()
      })
    },
    // Commita a seleção pendente (emite `apply`) e limpa o estado de busca —
    // chamado tanto pelo fechamento nativo do popover (watch de `show`) quanto
    // por `blur()` (fechamento forçado via $ref, ver abaixo).
    closeAndCommit() {
      if (this.loading || this.disabled) return
      this.hasBeenBlurred = true
      if (this.selectedChangedWhileOpen) {
        this.selectedChangedWhileOpen = false
        const out =
          this.effectiveReturnBy === 'value'
            ? this.getVModelOutByValue()
            : this.getVModelOutById()
        this.$emit('apply', out)
      }
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.validate(true)
      })
      this.clearDisplayed()
    },
    // Método público chamado via $ref pelo componente pai (ex: ao clicar em
    // "Aplicar" no menu de filtros) para fechar e commitar a seleção pendente
    // ANTES de ler/aplicar o draft. Roda de forma síncrona — ao contrário do
    // fechamento nativo por click-outside do Vuetify, que é assíncrono
    // (setTimeout(0) interno) e chega tarde demais para esse caso.
    blur() {
      if (!this.show) return
      this.closeAndCommit()
      this.show = false
    },
  },
}
</script>

<template>
  <div
    class="select-search"
    :style="{ maxWidth: normalizedMaxWidth, minWidth: normalizedMinWidth }"
  >
    <v-input
      ref="input"
      :value="validationValue"
      :rules="rules"
      :hide-details="!showDetails"
      :disabled="disabled"
      :loading="loading"
    >
      <v-menu
        v-model="show"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        :max-width="normalizedMenuMaxWidth"
        :min-width="menuMinWidth"
        min-height="auto"
        offset-y
        content-class="select-search__menu rounded-xs"
        :disabled="disabled"
      >
        <template v-slot:activator="{ on, attrs }">
          <div class="select-search__select">
            <span
              v-if="label"
              class="text-sm"
              :class="{ 'select-search__label--disabled': disabled }"
            >
              {{ label }}
            </span>
            <div
              ref="activator"
              class="select-search__input-container rounded-xs"
              :class="{
                'select-search__input-container--active': show,
                'select-search__input-container--disabled': disabled,
              }"
              v-bind="attrs"
              v-on="on"
            >
              <template v-if="$scopedSlots.selection">
                <slot name="selection" />
              </template>
              <template v-else-if="selectionText">
                <span class="select-search__selection-text text-xs">
                  {{ selectionText }}
                </span>
              </template>
              <template v-else>
                <SelectedItemsDisplay
                  :selectedItemsValues="selectedItemsValues"
                  :default-text="selectionPlaceholder"
                />
              </template>
              <v-icon
                class="select-search__icon"
                :class="{ 'select-search__icon--active': show }"
              >
                mdi-chevron-down
              </v-icon>
            </div>
          </div>
        </template>

        <div class="select-search__modal bg-white">
          <v-text-field
            ref="searchField"
            v-model="search"
            :placeholder="placeholder || searchPlaceholder"
            class="select-search__search new-ux"
            outlined
            autofocus
            :disabled="loading || disabled || isEmptySourceList"
          />

          <div
            v-if="showClearSelectedItems"
            class="text-primary text-xs text-underline text-bold text-right hover pr-4"
            @click="clearSelectedItems"
          >
            Limpar seleção
          </div>

          <div v-if="showSelectedItemsList && !single">
            <div class="select-search__title-selected text-xxs">
              Itens selecionados:
            </div>
            <div class="select-search__divider"></div>
          </div>

          <div
            v-if="showSelectedItemsList && !single"
            class="select-search__list-items select-search__list-items--permanent-selected"
          >
            <div v-for="item in permanentSelectedItems" :key="item.id">
              <div>
                <v-checkbox
                  v-model="selectedItems"
                  :value="item.id"
                  :label="$scopedSlots.item ? undefined : item.value"
                  hide-details
                  multiple
                  color="primary"
                  class="m-0 p-0"
                  @change="handleItemToggle(item.id)"
                >
                  <template v-if="$scopedSlots.item" v-slot:label>
                    <slot name="item" :item="item" />
                  </template>
                </v-checkbox>
              </div>
            </div>
          </div>

          <div
            v-if="showSelectedItemsList && !single"
            class="select-search__divider"
          ></div>

          <div v-if="loading" class="full-center pt-3">
            <v-progress-circular color="primary" indeterminate size="40" />
          </div>

          <div
            v-else-if="!isEmptyList && !isEmptySourceList"
            ref="listContainer"
            class="select-search__list-items"
            :class="{ 'select-search__list-items--full': single }"
            @scroll="handleScroll"
          >
            <div
              v-for="item in displayedItems"
              :key="item.id"
              :data-item-id="item.id"
            >
              <div v-if="!single">
                <v-checkbox
                  v-model="selectedItems"
                  :value="item.id"
                  :label="$scopedSlots.item ? undefined : item.value"
                  hide-details
                  multiple
                  color="primary"
                  class="m-0 p-0"
                  @change="handleItemToggle(item.id)"
                >
                  <template v-if="$scopedSlots.item" v-slot:label>
                    <slot name="item" :item="item" />
                  </template>
                </v-checkbox>
              </div>

              <div
                v-else
                class="select-search__option"
                :class="{
                  'select-search__option--selected': selectedItems.includes(
                    item.id
                  ),
                }"
                @click="selectSingle(item.id)"
              >
                <template v-if="$scopedSlots.item">
                  <slot name="item" :item="item" />
                </template>
                <template v-else>
                  {{ item.value }}
                </template>
              </div>
            </div>
          </div>

          <div
            v-else
            class="select-search__empty text-sm text-center text-color-900"
          >
            Nenhum item encontrado.
          </div>

          <div
            v-if="$scopedSlots.footer"
            class="d-flex align-center justify-center pt-3 select-search__footer"
          >
            <slot name="footer" />
          </div>
        </div>
      </v-menu>
    </v-input>
  </div>
</template>

<style lang="sass">
$select-search-border: 1px solid rgba(0, 0, 0, 0.24)
$select-search-base-spacing: 8px
$select-search-spacing: 16px
$disabled-opacity: .4

@mixin searchBorderWithHover()
  border: $select-search-border
  &:hover
    border: 1px solid var(--dark)
    cursor: pointer

@mixin selectSearchLabel()
  color: #8e8e8e !important
  font-weight: 600 !important
  font-size: 14px !important
  font-family: 'GeneralSans','Geist','OpenSans'

.select-search
  flex-grow: 1

  .v-input.v-input--has-state.error--text .select-search__input-container
    border-color: var(--error) !important

  .v-input.v-input--has-state.error--text .select-search__icon
    color: var(--error) !important

  &__menu
    margin-top: 6px
    border: $select-search-border

  &__select
    width: 100%
    display: flex
    flex-direction: column
    gap: 6px

  &__label
    &--disabled
      opacity: $disabled-opacity

  &__input-container
    display: flex
    justify-content: space-between
    align-items: center
    padding: 4px 8px
    // Altura padrão dos controles (36px), igual ao BaseButton/inputs.
    height: var(--control-height)
    width: 100%
    min-width: 125px
    @include searchBorderWithHover()

    &--active
      border-color: var(--primary) !important

    &--disabled
      opacity: $disabled-opacity
      cursor: not-allowed
      pointer-events: none

  &__icon
    transition: transform 0.3s ease
    &--active
      transform: rotate(180deg)
      color: var(--primary) !important

  &__modal
    padding-block: 16px
    width: 100%
    display: flex
    flex-direction: column
    gap: $select-search-base-spacing

    .v-text-field__details
      display: none

    .v-input__slot
      margin-bottom: 0

  &__search
    padding-inline: $select-search-spacing !important
    padding-bottom: 0 !important

  &__list-items
    @include selectSearchLabel()
    display: flex
    flex-direction: column
    gap: 10px
    padding: $select-search-base-spacing $select-search-spacing
    margin-right: $select-search-spacing
    height: 50%
    max-height: 250px
    overflow: auto

    &--permanent-selected
      max-height: 150px

    &--full
      margin: 0px
      padding: 0px
      gap: 0px

    .v-label
      @include selectSearchLabel()

  &__option
    padding: 14px $select-search-spacing
    border-radius: 4px
    user-select: none
    cursor: pointer

    &:hover
      color: var(--primary)

  &__option--selected
    background-color: var(--primary-lighter)
    color: var(--primary)

  &__divider
    margin-inline: $select-search-spacing
    border-top: 0.5px solid var(--primary)

  &__title-selected
    padding-bottom: $select-search-base-spacing
    margin-inline: $select-search-spacing

  &__selection-text
    flex: 1
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__empty
    padding-inline: $select-search-spacing

  &__footer
    border-top: 1px solid rgba(0, 0, 0, 0.08)
</style>
